import React, { useState, useEffect } from 'react';
import { useAuth } from '../../api/AuthContext';
import Sidebar from '../../components/Sidebar';
import LoaderOverlay from '../../components/LoaderOverlay';
import InformacionGeneral from './InformacionGeneral';
import Horario from './Horario';
import Calificaciones from './Calificaciones';
import EstadoCuenta from './EstadoCuenta';
import FooterPortal from './FooterPortal';
import { getStudentMe, getStudents, getStudent, getDocumentosEstudiante } from '../../api/students';
import { getPeriodoActual } from '../../api/periodoActual';
import { getEstudianteBecas } from '../../api/becas';
import { getTurnoLabel } from '../../constants/turnos';
import { getModalidadLabel } from '../../constants/modalidades';
import styles from '../../styles/PortalEstudiantesStyles/PortalEstudiantes.module.css';

/**
 * Portal de Estudiantes
 * 
 * Identifica al estudiante a partir del usuario autenticado (email)
 * y carga toda su información real desde la base de datos.
 */

function PortalEstudiantesScreen() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('informacion');
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodoActual, setPeriodoActual] = useState('');
  const [fotoPerfilUrl, setFotoPerfilUrl] = useState('');
  const [becaActiva, setBecaActiva] = useState(null);

  // Buscar estudiante por email institucional del usuario autenticado
  useEffect(() => {
    const cargarDatosEstudiantePorEmail = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('[DEBUG] No hay token en localStorage. El usuario no está autenticado o el token expiró.');
      }
      if (!user || !user.email) {
        setLoading(false);
        setError('No hay usuario autenticado.');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        let estudiante = null;
        // Si el backend bloquea expl\u00edcitamente el acceso (estatus != ACTIVO/PASANTE),
        // /estudiantes/me/ responder\u00e1 403 con un mensaje espec\u00edfico. En ese caso
        // NO debemos seguir intentando con b\u00fasqueda manual: hay que cortar el portal.
        let accesoBloqueado = false;
        let mensajeBloqueo = null;

        // ESTRATEGIA 1: Intentar endpoint directo /estudiantes/me/
        console.log('[DEBUG] Intentando getStudentMe()...');
        try {
          const datosMe = await getStudentMe();
          if (datosMe && datosMe.id_estudiante) {
            console.log('[DEBUG] getStudentMe() exitoso:', datosMe);
            estudiante = datosMe;
          }
        } catch (meErr) {
          const status = meErr?.response?.status;
          const data = meErr?.response?.data || {};
          if (status === 403) {
            accesoBloqueado = true;
            mensajeBloqueo = data.error || 'Tu cuenta no tiene acceso al portal de estudiantes en este momento.';
            console.warn('[PORTAL] Acceso bloqueado por backend:', mensajeBloqueo);
          } else {
            console.log('[DEBUG] getStudentMe() fall\u00f3, intentando b\u00fasqueda manual...', meErr);
          }
        }

        if (accesoBloqueado) {
          setError(mensajeBloqueo);
          setStudentData(null);
          return;
        }
        
        // ESTRATEGIA 2: Búsqueda manual flexible por email (institucional O personal)
        if (!estudiante) {
          console.log('[DEBUG] Buscando estudiante por email:', user.email);
          const response = await getStudents({ search: user.email, no_pagination: 1 });
          const lista = Array.isArray(response) ? response : (response.results || []);
          
          console.log('[DEBUG] Resultados de búsqueda:', lista.length, 'estudiantes encontrados');
          
          // Buscar coincidencia flexible: primero email_institucional, luego email_personal
          const userEmailLower = user.email.toLowerCase().trim();
          estudiante = lista.find(e => {
            const instEmail = (e.email_institucional || '').toLowerCase().trim();
            const perEmail = (e.email_personal || '').toLowerCase().trim();
            return instEmail === userEmailLower || perEmail === userEmailLower;
          });
          
          if (!estudiante && lista.length > 0) {
            console.warn('[DEBUG] No se encontró coincidencia exacta, usando primer resultado');
            estudiante = lista[0];
          }
        }
        
        if (!estudiante) {
          console.error('[DEBUG] No se encontró ningún estudiante para:', user.email);
          setError('No se encontró información del estudiante para este usuario. Verifica que tu cuenta esté vinculada correctamente.');
          setStudentData(null);
        } else {
          // Validaci\u00f3n de estatus en frontend (defensa en profundidad).
          // La verificaci\u00f3n autoritativa la hace el backend en /estudiantes/me/, pero si
          // ese endpoint no estuvo disponible y caemos por la b\u00fasqueda manual, igual
          // bloqueamos aqu\u00ed los estatus que no permiten ingresar al portal.
          const ESTATUS_CON_ACCESO_PORTAL = ['ACTIVO', 'PASANTE'];
          const estatusActual = String(estudiante.estatus_estudiante || '').toUpperCase();
          if (estatusActual && !ESTATUS_CON_ACCESO_PORTAL.includes(estatusActual)) {
            const mensajesEstatus = {
              BAJA_TEMPORAL: 'Tu cuenta se encuentra en BAJA TEMPORAL. Ac\u00e9rcate a Servicios Escolares para reactivar tu acceso al portal.',
              BAJA_DEFINITIVA: 'Tu cuenta se encuentra en BAJA DEFINITIVA. No es posible acceder al portal de estudiantes.',
              EGRESADO: 'Tu cuenta figura como EGRESADO. El portal de estudiantes no se encuentra disponible para tu estatus.',
              NO_INSCRITO: 'No est\u00e1s inscrito actualmente. Ac\u00e9rcate a Servicios Escolares para regularizar tu inscripci\u00f3n.',
            };
            setError(mensajesEstatus[estatusActual] || 'Tu cuenta no tiene acceso al portal de estudiantes en este momento.');
            setStudentData(null);
            return;
          }

          console.log('[DEBUG] Estudiante encontrado:', estudiante.id_estudiante);
          let detalle = estudiante;
          // Intentar obtener detalle completo; si falla usar datos base para no romper el portal
          try {
            detalle = await getStudent(estudiante.id_estudiante);
          } catch (detalleErr) {
            console.warn('[DEBUG] getStudent() falló, usando datos base del estudiante:', detalleErr);
          }
          const datosPortal = mapearDatosEstudiante(detalle);
          setStudentData(datosPortal);
          console.log('[DEBUG] Datos del estudiante cargados exitosamente');
        }
      } catch (err) {
        console.error('[ERROR] Error al cargar datos del estudiante:', err);
        setError('Error al cargar la información del estudiante. Intenta recargando la página.');
        setStudentData(null);
      } finally {
        setLoading(false);
      }
    };
    cargarDatosEstudiantePorEmail();
  }, [user]);

  // Cargar periodo actual (vigente)
  useEffect(() => {
    const fetchPeriodoActual = async () => {
      try {
        const ciclo = await getPeriodoActual();
        setPeriodoActual(ciclo ? ciclo.nombre_ciclo || ciclo.name : '—');
      } catch (e) {
        setPeriodoActual('—');
      }
    };
    fetchPeriodoActual();
  }, []);

  // Cargar beca activa del estudiante
  useEffect(() => {
    const cargarBecaActiva = async () => {
      if (!studentData || !studentData.id) {
        setBecaActiva(null);
        return;
      }

      try {
        const dataBeca = await getEstudianteBecas(studentData.id);
        if (dataBeca && dataBeca.beca_activa) {
          setBecaActiva(dataBeca.beca_activa);
        } else {
          setBecaActiva(null);
        }
      } catch (error) {
        console.error('[ERROR] Error al cargar beca activa:', error);
        setBecaActiva(null);
      }
    };
    cargarBecaActiva();
  }, [studentData?.id]);

  // Cargar foto de perfil del estudiante (solo lectura).
  // Prioriza id_estudiante; si no existe, intenta resolver por matrícula.
  useEffect(() => {
    const resolveFotoPerfil = async () => {
      if (!studentData) {
        setFotoPerfilUrl('');
        return;
      }

      const getApiBase = () => {
        const envBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
        if (envBase) return envBase;
        if (typeof window !== 'undefined' && window.location?.origin) {
          return `${window.location.origin}/api`;
        }
        return '';
      };

      const getAbsoluteUrl = (doc) => {
        if (!doc) return '';
        if (doc.archivo_url) return doc.archivo_url;
        if (doc.url) {
          const mediaBase = getApiBase().replace(/\/api$/, '');
          return `${mediaBase}${doc.url}`;
        }
        if (doc.ruta_archivo) {
          const mediaBase = getApiBase().replace(/\/api$/, '');
          return `${mediaBase}/media/${doc.ruta_archivo}`;
        }
        return '';
      };

      try {
        let estudianteId = studentData?._raw?.id_estudiante || studentData?.id || null;

        if (!estudianteId && studentData?.matricula) {
          const response = await getStudents({ search: studentData.matricula, no_pagination: 1 });
          const lista = Array.isArray(response) ? response : (response.results || []);
          const match = lista.find((e) => String(e.matricula || '').trim() === String(studentData.matricula || '').trim());
          estudianteId = match?.id_estudiante || match?.id || null;
        }

        if (!estudianteId) {
          setFotoPerfilUrl(studentData?._raw?.foto_perfil_url || '');
          return;
        }

        const data = await getDocumentosEstudiante(estudianteId);
        const list = Array.isArray(data) ? data : (data?.results || []);
        const fotoDoc = list.find((d) => d.tipo_documento === 'FOTO_PERFIL') || list.find((d) => d.tipo_documento === 'PROFILE');

        const url = getAbsoluteUrl(fotoDoc) || studentData?._raw?.foto_perfil_url || '';
        if (url) {
          const sep = url.includes('?') ? '&' : '?';
          setFotoPerfilUrl(`${url}${sep}v=${Date.now()}`);
        } else {
          setFotoPerfilUrl('');
        }
      } catch {
        setFotoPerfilUrl(studentData?._raw?.foto_perfil_url || '');
      }
    };

    resolveFotoPerfil();
  }, [studentData]);

  // Avance de créditos real, calculado a partir del Cardex del estudiante
  // (mismo endpoint que usa Trámites Escolares para el Kardex oficial).
  useEffect(() => {
    const cargarAvanceCreditos = async () => {
      const estudianteId = studentData?._raw?.id_estudiante || studentData?.id;
      if (!estudianteId) return;

      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
      try {
        const res = await fetch(`${API_BASE}/escolar/kardex/${estudianteId}/`);
        if (!res.ok) return;
        const data = await res.json();
        const resumen = data?.resumen || {};
        const creditosObtenidos = resumen.creditos_obtenidos ?? 0;
        const creditosTotales = resumen.creditos_totales ?? 0;
        const avanceTexto = `${creditosObtenidos} / ${creditosTotales} Créditos`;
        
        // Calcular porcentaje de avance de créditos (limitado entre 0-100)
        const porcentajeAvance = creditosTotales > 0 
          ? Math.round(Math.min(100, Math.max(0, (creditosObtenidos / creditosTotales) * 100)) * 100) / 100
          : 0;

        setStudentData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            avance: porcentajeAvance,  // Guardar el porcentaje para la barra visual
            programa: { ...prev.programa, avanceCreditos: avanceTexto }
          };
        });
      } catch (e) {
        console.warn('[DEBUG] No se pudo cargar el avance de créditos desde el Cardex:', e);
      }
    };

    cargarAvanceCreditos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData?.id, studentData?._raw?.id_estudiante]);

  /**
   * Mapea los datos del estudiante del backend al formato del portal
   * @param {Object} estudiante - Datos del estudiante desde el API
   * @returns {Object} Datos formateados para el portal
   */
  const mapearDatosEstudiante = (estudiante) => {
    return {
      id: estudiante.id_estudiante,
      nombre: estudiante.nombre_completo || 'Sin nombre',
      matricula: estudiante.matricula || 'Sin matrícula',
      email: estudiante.email_institucional || estudiante.email_personal || 'Sin email',
      telefono: estudiante.telefono_principal || 'Sin teléfono',
      estatus: estudiante.estatus_estudiante || 'ACTIVO',
      
      // Datos personales
      curp: estudiante.curp,
      fotoPerfilUrl: estudiante.foto_perfil_url || '',
      fechaNacimiento: estudiante.fecha_nacimiento,
      lugarNacimiento: estudiante.lugar_nacimiento,
      sexo: estudiante.sexo === 'M' ? 'Masculino' : estudiante.sexo === 'F' ? 'Femenino' : estudiante.sexo,
      estadoCivil: estudiante.estado_civil,
      nacionalidad: estudiante.nacionalidad,
      paisOrigen: estudiante.pais_origen,
      
      // Contacto
      emailPersonal: estudiante.email_personal,
      emailInstitucional: estudiante.email_institucional,
      telefonoPrincipal: estudiante.telefono_principal,
      telefonoEmergencia: estudiante.telefono_emergencia,
      contactoEmergencia: estudiante.contacto_emergencia,
      
      // Dirección
      calle: estudiante.calle,
      numeroInt: estudiante.numero_int,
      numeroExt: estudiante.numero_ext,
      colonia: estudiante.colonia,
      ciudad: estudiante.ciudad,
      estado: estudiante.estado,
      codigoPostal: estudiante.codigo_postal,
      pais: estudiante.pais || 'México',
      
      // Información académica
      programa: {
        id: estudiante.id_programa_actual,
        nombre: estudiante.programa_nombre || 'Sin programa asignado',
        nivel: estudiante.programa_nivel || 'Pendiente',
        campus: estudiante.campus_nombre || 'Sin campus asignado',
        ingreso: estudiante.ciclo_ingreso_nombre || 'Pendiente',
        // Turno académico (solo lectura desde el portal del estudiante)
        turno: getTurnoLabel(estudiante.turno, 'No especificado'),
        // Modalidad académica (solo lectura)
        modalidad: getModalidadLabel(estudiante.modalidad, 'No especificada'),
        // TODO: Calcular avance de créditos desde inscripciones/plan de estudios
        avanceCreditos: 'Pendiente'
      },
      
      // Campos pendientes - Se conectarán cuando existan los módulos
      periodoActual: estudiante.periodo_inicial || 'Pendiente',
      avance: 0, // TODO: Calcular desde inscripciones y plan de estudios
      
      // Estado financiero - TODO: Conectar con módulo financiero
      saldoPendiente: 0,
      alertaSaldo: null,
      
      // Datos originales del backend para uso en subcomponentes
      _raw: estudiante
    };
  };


  if (loading) {
    return (
      <div className={styles.layout} style={{ position: 'relative' }}>
        <LoaderOverlay visible={true} label={'Cargando información del estudiante...'} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.layout}>
        <div className={styles.loadingContainer}>
          <p style={{ color: '#c00' }}>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }


  if (!studentData) {
    return (
      <div className={styles.layout}>
        <div className={styles.loadingContainer}>
          <p>No se encontró información del estudiante para este usuario.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.portalContainer}>
      {/* Header eliminado */}

      {/* Selector temporal de estudiante eliminado */}

      {/* Información del estudiante */}
      <div className={styles.studentInfoCard}>
        <div className={styles.studentHeader}>
          <div className={styles.avatarCircle}>
            {fotoPerfilUrl ? (
              <img
                src={fotoPerfilUrl}
                alt="Foto de perfil"
                className={styles.avatarImage}
                onError={() => setFotoPerfilUrl('')}
              />
            ) : (
              <span className={styles.avatarIcon}>👤</span>
            )}
          </div>
          <div className={styles.studentDetails}>
            <h2 className={styles.studentName}>{studentData.nombre}</h2>
            <div className={styles.studentMeta}>
              <span className={styles.metaItem}>
                <strong>ID:</strong> {studentData.id}
              </span>
              <span className={styles.metaItem}>
                <strong>Matrícula:</strong> {studentData.matricula}
              </span>
              <span className={`${styles.statusBadge} ${studentData.estatus === 'ACTIVO' ? styles.statusActive : styles.statusInactive}`}>
                {studentData.estatus}
              </span>
            </div>
            <div className={styles.contactInfo}>
              <span className={styles.contactItem}>
                ✉️ {studentData.email}
              </span>
              <span className={styles.contactItem}>
                📞 {studentData.telefono}
              </span>
            </div>
          </div>
            <div className={styles.statsCards}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Periodo Actual</span>
                <span className={styles.statValue}>
                  {periodoActual}
                </span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Avance</span>
                <span className={styles.statValue}>
                  {studentData.avance > 0 ? `${studentData.avance.toFixed(2)}%` : 'Pendiente'}
                </span>
              </div>
              {becaActiva && (
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Beca Activa</span>
                  <span className={styles.statValue}>
                    {becaActiva.nombre_beca}
                  </span>
                </div>
              )}
            </div>
        </div>

        {/* Alerta de saldo pendiente */}
        {studentData.saldoPendiente > 0 && (
          <div className={styles.alertBox}>
            <span className={styles.alertIcon}>⚠️</span>
            <div className={styles.alertContent}>
              <strong className={styles.alertTitle}>Saldo vencido</strong>
              <p className={styles.alertMessage}>
                {studentData.alertaSaldo || `Tienes un saldo pendiente de $${studentData.saldoPendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Información del programa */}
      <div className={styles.programCard}>
        <div className={styles.programHeader}>
          <div className={styles.programInfo}>
            <span className={styles.programLabel}>PROGRAMA</span>
            <h3 className={styles.programName}>{studentData.programa.nombre}</h3>
            <p className={styles.programLevel}>{studentData.programa.nivel}</p>
          </div>
          <div className={styles.campusInfo}>
            <span className={styles.campusLabel}>CAMPUS</span>
            <h3 className={styles.campusName}>{studentData.programa.campus}</h3>
            <p className={styles.campusIngreso}>Ingreso: {studentData.programa.ingreso}</p>
            <p className={styles.campusIngreso}>Turno: {studentData.programa.turno}</p>
            <p className={styles.campusIngreso}>Modalidad: {studentData.programa.modalidad}</p>
          </div>
        </div>
        <div className={styles.creditProgress}>
          <span className={styles.creditLabel}>AVANCE DE CRÉDITOS</span>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${studentData.avance || 0}%` }}
            />
          </div>
          <span className={styles.creditText}>{studentData.programa.avanceCreditos}</span>
        </div>
      </div>

      {/* Tabs de navegación */}
      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tab} ${activeTab === 'informacion' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('informacion')}
        >
          Información General
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'horario' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('horario')}
        >
          Horario
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'calificaciones' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('calificaciones')}
        >
          Calificaciones
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'estado-cuenta' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('estado-cuenta')}
        >
          Estado de Cuenta
        </button>
      </div>

      {/* Contenido de los tabs */}
      <div className={styles.tabContent}>
        {activeTab === 'informacion' && <InformacionGeneral studentData={studentData} />}
        {activeTab === 'horario' && <Horario studentData={studentData} />}
        {activeTab === 'calificaciones' && <Calificaciones studentData={studentData} />}
        {activeTab === 'estado-cuenta' && (
          <EstadoCuenta 
            studentData={studentData}
            periodoActual={periodoActual}
            usuario={user}
          />
        )}
      </div>

      {/* Footer del portal con información de contacto, mapa y aviso de cookies */}
      <FooterPortal />
      </div>
    </div>
  );
}

export default PortalEstudiantesScreen;
